import React, { createContext, useState, useEffect, useCallback } from 'react'
import { z } from 'zod'
import { authApi } from '@/api/auth'
import type { User, LoginCredentials } from '@/types/auth'

/** Zod schema for validating User data from localStorage */
const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  role: z.enum(['ADMIN', 'SUPPORT', 'VIEWER']),
})

/**
 * Safely parse stored user data from localStorage.
 * Returns null and clears storage if data is invalid.
 */
function parseStoredUser(): User | null {
  try {
    const raw = localStorage.getItem('auth_user')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const result = userSchema.safeParse(parsed)
    if (result.success) {
      return result.data
    }
    // Invalid shape — clear corrupted data
    console.warn('Auth: cleared invalid user data from localStorage', result.error.issues)
    localStorage.removeItem('auth_user')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    return null
  } catch {
    // JSON parse failed — clear corrupted data
    console.warn('Auth: cleared unparseable user data from localStorage')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    return null
  }
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem('auth_token')
    if (token) {
      const storedUser = parseStoredUser()
      if (storedUser) {
        setUser(storedUser)
      }
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials)
    if (response.success && response.data.accessToken && response.data.user) {
      // Store access token for API calls (not ID token)
      localStorage.setItem('auth_token', response.data.accessToken)
      localStorage.setItem('refresh_token', response.data.refreshToken!)
      localStorage.setItem('auth_user', JSON.stringify(response.data.user))
      setUser(response.data.user)
    }
  }, [])

  const logout = useCallback(() => {
    authApi.logout()
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('auth_user')
    setUser(null)
  }, [])

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
